import React, { useState } from 'react';
import { SystemConfig } from '../../data/types';

interface SecuritySettingsTabProps {
  config: SystemConfig;
  onSave: (updatedConfig: Partial<SystemConfig>) => void;
  saving: boolean;
}

const SecuritySettingsTab: React.FC<SecuritySettingsTabProps> = ({ config, onSave, saving }) => {
  const [passwordPolicy, setPasswordPolicy] = useState({
    minLength: config.securitySettings.passwordPolicy.minLength,
    requireUppercase: config.securitySettings.passwordPolicy.requireUppercase,
    requireLowercase: config.securitySettings.passwordPolicy.requireLowercase,
    requireNumbers: config.securitySettings.passwordPolicy.requireNumbers,
    requireSpecialChars: config.securitySettings.passwordPolicy.requireSpecialChars,
    expiryDays: config.securitySettings.passwordPolicy.expiryDays
  });
  
  const [sessionTimeout, setSessionTimeout] = useState(config.securitySettings.sessionTimeout);
  const [maxLoginAttempts, setMaxLoginAttempts] = useState(config.securitySettings.maxLoginAttempts);
  const [lockoutDuration, setLockoutDuration] = useState(config.securitySettings.lockoutDuration);

  const handleSave = () => {
    const updatedConfig: Partial<SystemConfig> = {
      securitySettings: {
        ...config.securitySettings,
        passwordPolicy,
        sessionTimeout,
        maxLoginAttempts,
        lockoutDuration
      }
    };
    
    onSave(updatedConfig);
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">Security Settings</h2>
      
      {/* Password Policy */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Password Policy</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Password Length
            </label>
            <input
              type="number"
              min="6"
              max="20"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={passwordPolicy.minLength}
              onChange={(e) => setPasswordPolicy({
                ...passwordPolicy,
                minLength: parseInt(e.target.value) || 8
              })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password Expiry (Days)
            </label>
            <input
              type="number"
              min="0"
              max="365"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={passwordPolicy.expiryDays}
              onChange={(e) => setPasswordPolicy({
                ...passwordPolicy,
                expiryDays: parseInt(e.target.value) || 90
              })}
            />
            <p className="text-xs text-gray-500 mt-1">Set to 0 for no expiry</p>
          </div>
        </div>
        
        <div className="mt-3 space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireUppercase"
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              checked={passwordPolicy.requireUppercase}
              onChange={(e) => setPasswordPolicy({
                ...passwordPolicy,
                requireUppercase: e.target.checked
              })}
            />
            <label htmlFor="requireUppercase" className="ml-2 block text-sm text-gray-700">
              Require uppercase letters
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireLowercase"
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              checked={passwordPolicy.requireLowercase}
              onChange={(e) => setPasswordPolicy({
                ...passwordPolicy,
                requireLowercase: e.target.checked
              })}
            />
            <label htmlFor="requireLowercase" className="ml-2 block text-sm text-gray-700">
              Require lowercase letters
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireNumbers"
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              checked={passwordPolicy.requireNumbers}
              onChange={(e) => setPasswordPolicy({
                ...passwordPolicy,
                requireNumbers: e.target.checked
              })}
            />
            <label htmlFor="requireNumbers" className="ml-2 block text-sm text-gray-700">
              Require numbers
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireSpecialChars"
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              checked={passwordPolicy.requireSpecialChars}
              onChange={(e) => setPasswordPolicy({
                ...passwordPolicy,
                requireSpecialChars: e.target.checked
              })}
            />
            <label htmlFor="requireSpecialChars" className="ml-2 block text-sm text-gray-700">
              Require special characters
            </label>
          </div>
        </div>
      </div>
      
      {/* Session Settings */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Session Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Session Timeout (Minutes)
            </label>
            <input
              type="number"
              min="5"
              max="240"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={sessionTimeout}
              onChange={(e) => setSessionTimeout(parseInt(e.target.value) || 30)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Users will be automatically logged out after this period of inactivity
            </p>
          </div>
        </div>
      </div>
      
      {/* Login Attempt Settings */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Login Attempt Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maximum Login Attempts
            </label>
            <input
              type="number"
              min="1"
              max="10"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={maxLoginAttempts}
              onChange={(e) => setMaxLoginAttempts(parseInt(e.target.value) || 5)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Account will be temporarily locked after this many failed attempts
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Lockout Duration (Minutes)
            </label>
            <input
              type="number"
              min="5"
              max="60"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={lockoutDuration}
              onChange={(e) => setLockoutDuration(parseInt(e.target.value) || 15)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Duration for which accounts remain locked after exceeding maximum login attempts
            </p>
          </div>
        </div>
      </div>
      
      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className={`px-4 py-2 rounded text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
          }`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Security Settings'}
        </button>
      </div>
    </div>
  );
};

export default SecuritySettingsTab;
